import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useCampaignGamePages } from "@/hooks/use-campaign-game-pages";

/** Row height in px. Pinned rather than content-derived because the list is
 *  windowed — this number drives the window math and the scroll spacers, so a
 *  row that measured differently would drift the scrollbar. Kept tight (a 28px
 *  slot, 13px type) so a long list shows a dozen games at a time. */
const ROW_HEIGHT = 40;
/** One row: the prize table's cell (card-surface fill, inner radius), 38px + a 2px gap under it
 *  = the pinned ROW_HEIGHT, so the window math stays exact whatever precedes a row. */
const ROW_CLASS =
  "flex h-[38px] w-full shrink-0 items-center rounded-(--border-radius-inner) bg-background-card-surface px-3 mb-0.5";

/** Rows the list shows before it scrolls; a shorter list sizes the dialog to itself, down to a
 *  floor so a two-game list is still a dialog and not a stub under the search. */
const MAX_VISIBLE_ROWS = 11;
const MIN_VISIBLE_ROWS = 4;

/** Rows kept mounted above and below the viewport so a fast flick doesn't
 *  expose blank space before the next render lands. */
const OVERSCAN = 8;

/**
 * Searchable list of a campaign's eligible games (Figma 5102:763), opened from
 * the "+N games" chip in the Info tab. Shared by every campaign type, and by the
 * per-prize "free spins games" lists.
 *
 * Layout is a fixed header (title + search) + scrolling list + a bordered footer
 * holding the close button, so the close never floats over the list behind it.
 * The container size is fixed: a campaign's game list runs from a handful to tens
 * of thousands, and a dialog that grew and shrank with the result count jumped
 * around under the player's cursor as they typed.
 *
 * The list is **windowed** — only the rows in view are mounted, with spacers
 * standing in for the rest. A prod-shaped selection is ~40k games; mounting that
 * whole list put 80k nodes in the DOM and froze the widget on every keystroke,
 * which read as "search returns nothing".
 *
 * Two sources: `games` (the names are already on the client — a picked list, a prize's games) is
 * filtered locally; `campaignId` with no `games` (an "All games" campaign, whose names never ship)
 * fetches a page at a time and searches on the server (`useCampaignGamePages`), loading the next
 * page as the player nears the end of what is loaded.
 */
export const EligibleGamesDialog = ({
  games,
  campaignId,
  count,
  trigger,
  title,
}: {
  /** The names, when the client has them. Omit (with `campaignId`) to page them from the server. */
  games?: string[];
  /** The campaign whose eligible games are fetched page by page when `games` is omitted. */
  campaignId?: number;
  /** The real total for the header badge when `games` may be a preview subset. */
  count?: number;
  trigger: ReactNode;
  /** Dialog heading — e.g. "Free spins games" when the games are the ones a
   *  free-spins prize can be played on. Defaults to "Eligible games". */
  title?: string;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  const remote = games === undefined && campaignId != null;
  // Only while open: a closed dialog never asks the server for anything.
  const pages = useCampaignGamePages(remote && open ? campaignId : null, query);
  const local = useMemo(() => {
    const list = games ?? [];
    const q = query.trim().toLowerCase();
    return q ? list.filter((g) => g.toLowerCase().includes(q)) : list;
  }, [games, query]);
  const filtered = remote ? pages.names : local;
  // Remote: the first page hasn't landed yet (skeleton rows), and a trailing skeleton row while
  // the next page loads.
  const firstLoad = remote && pages.loading && filtered.length === 0;
  const tailRow = remote && pages.hasMore ? 1 : 0;
  const rowCount = filtered.length + tailRow;
  const total = count ?? (remote ? pages.total : filtered.length);
  // The box follows the FULL list (the real total for a remote one), never the search results.
  const sizingRows = remote
    ? (count ?? pages.total ?? MAX_VISIBLE_ROWS)
    : (games?.length ?? 0);

  // How many rows fit is a function of the list's height, so it has to be
  // measured. A callback ref rather than an effect keyed on `open`: the content
  // is portaled, and Radix's Portal renders nothing on its first pass unless it
  // is handed a container (the regular-DOM case — see PortalContainerContext),
  // so the list arrives in a second commit. An effect would run against the
  // first one, find no node, and never measure at all. A ref callback fires
  // whenever the node actually attaches, and still before paint.
  const measureList = useCallback((el: HTMLDivElement | null) => {
    listRef.current = el;
    if (!el) return;

    setViewport(el.clientHeight);
    const observer = new ResizeObserver(([entry]) =>
      setViewport(entry.contentRect.height)
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      listRef.current = null;
    };
  }, []);

  // Back to the top of the list. The DOM offset has to be cleared alongside our
  // own state: reopening the dialog before its exit animation finishes reuses
  // the same node, so state alone would render row 0 into an element still
  // scrolled to where the player left it — i.e. blank space.
  const resetScroll = () => {
    setScrollTop(0);
    if (listRef.current) listRef.current.scrollTop = 0;
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    // Reopen starts fresh. Resetting on open rather than on close keeps the
    // close animation showing what the player was looking at.
    if (next) {
      setQuery("");
      resetScroll();
    }
  };

  const handleSearch = (value: string) => {
    setQuery(value);
    // A new query is a new list; keeping the old offset would land the player
    // mid-way down (or past the end of) the results they just asked for.
    resetScroll();
  };

  const first = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const last = Math.min(
    rowCount,
    Math.ceil((scrollTop + viewport) / ROW_HEIGHT) + OVERSCAN
  );
  const dialogTitle = title ?? t("eligibleGames.dialogTitle", "Eligible games");

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      {/* Own scroll/padding layout (header + scrolling list) with DialogContent's floating
          close button on the corner. The height follows the FULL list — up to
          MAX_VISIBLE_ROWS, then it scrolls — so a short list gets a short dialog, while typing
          in the search never resizes the box under the player's cursor (the filtered count
          doesn't move it). DialogContent's max-height still clamps it on a short viewport. */}
      <DialogContent
        containerClassName="sm:max-w-md"
        // The cap on the SURFACE too: DialogContent's max-height sits on its wrapper, which a
        // content-sized surface would overflow on a short screen (a phone held sideways).
        className="max-h-[calc(100dvh-32px)] gap-0 overflow-hidden p-0 lg:max-h-[80vh]"
      >
        <div className="flex shrink-0 flex-col gap-3 px-5 pt-5 pb-3">
          <div className="flex items-center justify-between gap-3 pr-8">
            {/* The card-heading voice (uppercase, tracked), a step up for the dialog: it reads as
                the card it opened from, not a page title. */}
            <DialogTitle className="font-heading text-[12px] leading-4 font-semibold tracking-[0.96px] text-foreground uppercase lg:text-[13px]">
              {dialogTitle}
            </DialogTitle>
            {total != null && (
              <Badge variant="tonal" color="primary">
                {total.toLocaleString()}
              </Badge>
            )}
          </div>

          {/* The search sits in the glass track (as the tabs do), not a boxed form field. */}
          <div className="btn-glass flex h-11 w-full items-center gap-2 rounded-full px-4">
            <Search
              className="size-[18px] shrink-0 text-muted-foreground"
              strokeWidth={2}
            />
            <input
              type="text"
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={t("eligibleGames.searchPlaceholder", "Search games")}
              aria-label={t("eligibleGames.searchPlaceholder", "Search games")}
              // 16px on mobile stops iOS Safari auto-zooming on focus (it zooms
              // any input < 16px); the widget is embedded, so it can't rely on the
              // host page's viewport meta. Desktop keeps the 13px design.
              className="w-full min-w-0 bg-transparent font-heading text-[16px] font-semibold text-foreground outline-none placeholder:text-muted-foreground sm:text-[13px]"
            />
          </div>
        </div>

        {/* Only the window is mounted, so the row count is invisible to assistive
            tech — announce it instead. */}
        <span className="sr-only" role="status" aria-live="polite">
          {t("eligibleGames.count", "{{count}} games", {
            count: remote ? (pages.total ?? 0) : filtered.length,
          })}
        </span>

        <div
          ref={measureList}
          onScroll={(e) => {
            const el = e.currentTarget;
            setScrollTop(el.scrollTop);
            // Within ~a screen of the loaded end: fetch the next page before the player hits it.
            if (
              remote &&
              el.scrollTop + el.clientHeight >
                (filtered.length - MAX_VISIBLE_ROWS) * ROW_HEIGHT
            ) {
              pages.loadMore();
            }
          }}
          role="list"
          aria-label={dialogTitle}
          className="flex min-h-0 shrink grow-0 flex-col overflow-y-auto overscroll-y-contain px-3 pb-3"
          // pb-3 (12px) is inside the box, so it is added to the rows' height.
          style={{
            flexBasis:
              Math.max(
                MIN_VISIBLE_ROWS,
                Math.min(sizingRows, MAX_VISIBLE_ROWS)
              ) *
                ROW_HEIGHT +
              12,
          }}
        >
          {firstLoad ? (
            Array.from(
              { length: Math.max(MIN_VISIBLE_ROWS, Math.min(sizingRows, 6)) },
              (_, i) => <SkeletonRow key={i} striped={i % 2 === 1} />
            )
          ) : filtered.length === 0 ? (
            // Remote with no query and no names means the request failed: show nothing rather
            // than claim there are no games.
            query.trim() || !remote ? (
              <p className="px-2 py-[14px] font-body text-[14px] text-muted-foreground">
                {t("eligibleGames.noMatch", "No games match “{{query}}”.", {
                  query,
                })}
              </p>
            ) : null
          ) : (
            <>
              <div
                aria-hidden
                className="shrink-0"
                style={{ height: first * ROW_HEIGHT }}
              />
              {filtered
                .slice(first, Math.min(last, filtered.length))
                .map((game, i) => (
                  // Keyed by row index (not name): the window slides on every
                  // scroll, so index keys let React update text in place instead
                  // of remounting the rows — and game names can repeat.
                  <div
                    key={first + i}
                    role="listitem"
                    // The prize table's rows: every row a cell on the card-surface fill with a
                    // 2px gap, alternate rows carrying the table's left-fading stripe. Parity from
                    // the absolute index, so the stripes stay put as the window slides.
                    className={cn(
                      ROW_CLASS,
                      (first + i) % 2 === 1 && "row-stripe"
                    )}
                  >
                    <p className="min-w-0 truncate font-heading text-[13px] leading-[18px] font-medium text-foreground">
                      {game}
                    </p>
                  </div>
                ))}
              {/* The next page is on its way: a placeholder row where it will land. */}
              {tailRow > 0 && last >= rowCount && (
                <SkeletonRow striped={filtered.length % 2 === 1} />
              )}
              <div
                aria-hidden
                className="shrink-0"
                style={{ height: Math.max(0, rowCount - last) * ROW_HEIGHT }}
              />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** A row's shape with no name yet: a soft bar, while a page of names loads. */
const SkeletonRow = ({ striped }: { striped: boolean }) => (
  <div aria-hidden className={cn(ROW_CLASS, striped && "row-stripe")}>
    <span className="h-2.5 w-2/5 animate-pulse rounded-full bg-foreground/10 motion-reduce:animate-none" />
  </div>
);
